import PristineCtor from "pristinejs";
import { activeQRFormType, getQRFormFieldControls } from "./gm/qr_form_contracts";
import {
  buildLoginReturnURL,
  parseHXTriggerHeader,
  parsePayload as parseBrowserErrorPayload,
  shouldRedirectToLogin,
  toastVariant as toastVariantForError,
} from "./errors";

type FieldElement = HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement;
type ValidationError = { field: string; message: string };
type ValidateOptions = {
  focus?: boolean;
  renderSummary?: boolean;
};
type PristineFieldError = { input: FieldElement; errors: string[] };
type PristineInstance = {
  validate(input?: boolean | FieldElement | NodeList | FieldElement[], silent?: boolean): boolean;
  getErrors(input?: FieldElement): PristineFieldError[] | string[];
  reset(): void;
};
type PristineConstructor = new (
  form: HTMLFormElement,
  config?: Record<string, string>,
  live?: boolean
) => PristineInstance;
export type ValidationAPI = {
  init(root?: ParentNode): void;
  validateForm(form: HTMLFormElement, options?: ValidateOptions): boolean;
  validateField(input: FieldElement, options?: ValidateOptions): boolean;
  clearValidationState(form: HTMLFormElement): void;
  isValidationAwareForm(form: HTMLFormElement): boolean;
};

declare global {
  interface Window {
    __WMU_VALIDATE_BOOTED?: boolean;
    WMUValidation?: ValidationAPI;
  }
}

const Pristine = PristineCtor as unknown as PristineConstructor;
const doc = document;
const pristineByForm = new WeakMap<HTMLFormElement, PristineInstance>();
// ADR-0509: concurrency guard — tracks forms that are actively submitting
const submittingForms = new WeakSet<HTMLFormElement>();

function withNetworkProgress<T>(promise: Promise<T>): Promise<T> {
  const tracker = window.WMUNetworkProgress;
  if (!tracker || typeof tracker.track !== "function") {
    return promise;
  }
  return tracker.track(promise);
}

function boot() {
  initValidationForms(doc);
  bindHTMXValidationHandlers();
  initQRLiveValidateButtons();
}

function init(root: ParentNode = doc) {
  initValidationForms(root);
}

function initValidationForms(root: ParentNode) {
  root.querySelectorAll<HTMLFormElement>('form[data-validate], form[data-validation-form="true"]').forEach((form) => {
    bindForm(form);
  });
}

function bindForm(form: HTMLFormElement) {
  getPristine(form);
  bindFieldListeners(form);

  if (form.dataset.wmuValidateBound === "true") {
    return;
  }
  form.dataset.wmuValidateBound = "true";

  form.addEventListener("submit", (event) => {
    // ADR-0509: concurrency guard — block double-submits
    if (submittingForms.has(form)) {
      event.preventDefault();
      return;
    }

    const submitter = event.submitter instanceof HTMLElement ? event.submitter : null;

    if (!validateForm(form, { focus: true, renderSummary: true })) {
      event.preventDefault();
      return;
    }

    if (isHTMXManagedForm(form)) {
      submittingForms.add(form);
      form.setAttribute("aria-busy", "true");
      setSubmitting(form, true);
      return;
    }

    event.preventDefault();
    void submitWithValidation(form, submitter);
  });

  form.addEventListener("reset", () => {
    // ADR-0509: synchronous reset — remove the setTimeout race window
    clearSubmitting(form);
    const pristine = pristineByForm.get(form);
    pristine?.reset();
    clearValidationState(form);
  });
}

function bindFieldListeners(form: HTMLFormElement) {
  form.querySelectorAll<FieldElement>("input, textarea, select").forEach((input) => {
    if (input.dataset.wmuValidateFieldBound === "true") {
      return;
    }
    input.dataset.wmuValidateFieldBound = "true";

    const revalidate = () => {
      if (shouldValidateLive(input)) {
        validateField(input, { renderSummary: false });
      }
    };

    input.addEventListener("blur", () => {
      validateField(input, { renderSummary: false });
    });
    input.addEventListener("input", revalidate);
    input.addEventListener("change", revalidate);
  });
}

function shouldValidateLive(input: FieldElement) {
  return input.classList.contains("input-invalid")
    || input.getAttribute("aria-invalid") === "true";
}

function isValidationAwareForm(form: HTMLFormElement) {
  return form.matches('[data-validate], [data-validation-form="true"]')
    || form.querySelector("[data-field], [data-field-error-for], [data-validation-summary]") !== null;
}

function isHTMXManagedForm(form: HTMLFormElement) {
  return form.hasAttribute("hx-post")
    || form.hasAttribute("hx-put")
    || form.hasAttribute("hx-patch")
    || form.hasAttribute("hx-delete");
}

function getPristine(form: HTMLFormElement) {
  const cached = pristineByForm.get(form);
  if (cached) {
    return cached;
  }

  prepareFormForPristine(form);
  const pristine = new Pristine(
    form,
    {
      classTo: "form-group",
      errorClass: "has-error",
      successClass: "has-success",
      errorTextParent: "form-group",
      errorTextTag: "span",
      errorTextClass: "field-error pristine-error",
    },
    false
  );
  pristineByForm.set(form, pristine);
  return pristine;
}

function prepareFormForPristine(form: HTMLFormElement) {
  form.querySelectorAll<FieldElement>("[data-validate]").forEach((input) => {
    const rules = String(input.dataset.validate || "")
      .split("|")
      .map((rule) => rule.trim())
      .filter(Boolean);

    for (const rule of rules) {
      const [name, rawValue] = rule.split(":");
      const value = String(rawValue || "").trim();

      if (name === "required" && !input.hasAttribute("required")) {
        input.setAttribute("required", "");
        continue;
      }

      if (!value) {
        continue;
      }

      const isNumeric = input instanceof HTMLInputElement
        && ["number", "range"].includes(input.type);

      if (name === "min") {
        if (isNumeric) {
          if (!input.hasAttribute("min")) {
            input.setAttribute("min", value);
          }
        } else if (!input.dataset.pristineMinlength && !input.hasAttribute("minlength")) {
          input.dataset.pristineMinlength = value;
        }
        continue;
      }

      if (name === "max") {
        if (isNumeric) {
          if (!input.hasAttribute("max")) {
            input.setAttribute("max", value);
          }
        } else if (!input.dataset.pristineMaxlength && !input.hasAttribute("maxlength")) {
          input.dataset.pristineMaxlength = value;
        }
        continue;
      }

      if (name === "pattern" && !input.dataset.pristinePattern && !input.hasAttribute("pattern")) {
        input.dataset.pristinePattern = value;
      }
    }
  });
}

function validateForm(form: HTMLFormElement, options: ValidateOptions = {}) {
  clearValidationState(form);

  const pristine = getPristine(form);
  pristine.validate(undefined, true);
  const errors = collectFormErrors(form, pristine);

  if (errors.length > 0) {
    renderValidationErrors(form, errors, options.renderSummary !== false);
    if (options.focus !== false) {
      focusFirstInvalidField(form);
    }
    return false;
  }

  return true;
}

function validateField(input: FieldElement, options: ValidateOptions = {}) {
  const form = input.form;
  if (!(form instanceof HTMLFormElement)) {
    return true;
  }

  const fieldKey = resolveFieldKey(input.getAttribute("name") || input.id || "request");
  clearFieldState(form, fieldKey);

  const pristine = getPristine(form);
  const errors = input instanceof HTMLInputElement && input.type === "hidden"
    ? collectHiddenInputErrors(input)
    : collectPristineFieldErrors(pristine, input);

  if (errors.length === 0) {
    return true;
  }

  renderValidationErrors(form, errors.map((message) => ({ field: fieldKey, message })), options.renderSummary === true);
  return false;
}

function collectFormErrors(form: HTMLFormElement, pristine: PristineInstance) {
  const errors: ValidationError[] = [];
  const seen = new Set<string>();
  const pristineErrors = pristine.getErrors();

  if (Array.isArray(pristineErrors)) {
    pristineErrors.forEach((entry) => {
      if (
        !entry
        || typeof entry !== "object"
        || !("input" in entry)
        || !Array.isArray(entry.errors)
        || entry.errors.length === 0
      ) {
        return;
      }
      const field = resolveFieldKey(entry.input.getAttribute("name") || entry.input.id || "request");
      const message = String(entry.errors[0] || "").trim();
      if (!message) {
        return;
      }
      const key = `${field}|${message}`;
      if (seen.has(key)) {
        return;
      }
      seen.add(key);
      errors.push({ field, message });
    });
  }

  form.querySelectorAll<HTMLInputElement>('input[type="hidden"][data-validate]').forEach((input) => {
    collectHiddenInputErrors(input).forEach((message) => {
      const field = resolveFieldKey(input.getAttribute("name") || input.id || "request");
      const key = `${field}|${message}`;
      if (seen.has(key)) {
        return;
      }
      seen.add(key);
      errors.push({ field, message });
    });
  });

  return errors;
}

function collectPristineFieldErrors(pristine: PristineInstance, input: FieldElement) {
  pristine.validate(input, true);
  const raw = pristine.getErrors(input);
  return Array.isArray(raw)
    ? raw.filter((message): message is string => typeof message === "string" && message.trim().length > 0)
    : [];
}

function collectHiddenInputErrors(input: HTMLInputElement) {
  const rules = String(input.dataset.validate || "")
    .split("|")
    .map((rule) => rule.trim())
    .filter(Boolean);

  const value = String(input.value || "").trim();
  const errors: string[] = [];

  for (const rule of rules) {
    const [name, rawValue] = rule.split(":");
    const limit = Number.parseInt(String(rawValue || ""), 10);

    if (name === "required" && value === "") {
      errors.push("This field is required");
      break;
    }
    if (value === "") {
      continue;
    }
    if (name === "min" && Number.isFinite(limit) && value.length < limit) {
      errors.push(`Minimum ${limit} characters.`);
      break;
    }
    if (name === "max" && Number.isFinite(limit) && value.length > limit) {
      errors.push(`Maximum ${limit} characters.`);
      break;
    }
  }

  return errors;
}

async function submitWithValidation(form: HTMLFormElement, submitter: HTMLElement | null) {
  // NOTE: validation is performed by the submit event handler before calling this function.
  // A second validateForm call here is omitted to avoid double-validation which can cause
  // Pristine to report a stale invalid state and skip the aria-busy guard (ADR-0509).

  // ADR-0509: mark as submitting before async work
  submittingForms.add(form);
  form.setAttribute("aria-busy", "true");
  setSubmitting(form, true);
  try {
    const action = buildActionURL(form);
    const method = (form.getAttribute("method") || "POST").toUpperCase();
    const request = buildRequestPayload(form, submitter);

    const response = await withNetworkProgress(fetch(action, {
      method,
      credentials: "same-origin",
      headers: request.headers,
      body: request.body,
    }));

    const payload = await parseJSONSafe(response);
    const errorDetail = parseErrorDetail(payload, response);

    if (response.status === 422 || errorDetail.fields.length > 0 && errorDetail.descriptor?.ux_treatment === "inline_form") {
      renderValidationErrors(form, errorDetail.fields, true);
      focusFirstInvalidField(form);
      toast(errorDetail.message || "Please fix the highlighted fields.", toastVariantForError(errorDetail));
      return;
    }

    const hxRedirect = response.headers.get("HX-Redirect");
    if (hxRedirect) {
      clearSubmitting(form);
      window.location.assign(hxRedirect);
      return;
    }

    if (response.redirected && response.url) {
      clearSubmitting(form);
      window.location.assign(response.url);
      return;
    }

    if (response.ok) {
      clearSubmitting(form);
      window.location.reload();
      return;
    }

    if (shouldRedirectToLogin(errorDetail)) {
      clearSubmitting(form);
      window.location.assign(buildLoginReturnURL());
      return;
    }

    renderValidationSummary(form, [{ field: "request", message: errorDetail.message }]);
    toast(errorDetail.message, toastVariantForError(errorDetail));
  } catch {
    const message = "Network error. Please retry.";
    renderValidationSummary(form, [{ field: "request", message }]);
    toast(message, "error");
  } finally {
    // ADR-0509: always clear submitting state (success, error, or network failure)
    clearSubmitting(form);
  }
}

function buildRequestPayload(form: HTMLFormElement, submitter: HTMLElement | null) {
  const fd = new FormData(form);
  if (submitter && submitter.getAttribute("name")) {
    fd.append(submitter.getAttribute("name") || "", submitter.getAttribute("value") || "");
  }

  let hasBinary = false;
  fd.forEach((value) => {
    if (value instanceof File && value.size > 0) {
      hasBinary = true;
    }
  });

  if (hasBinary) {
    return {
      headers: {
        Accept: "application/json",
      } as Record<string, string>,
      body: fd,
    };
  }

  const body = new URLSearchParams();
  fd.forEach((value, key) => {
    if (value instanceof File) {
      body.append(key, value.name || "");
      return;
    }
    body.append(key, String(value));
  });

  return {
    headers: {
      Accept: "application/json",
      "Content-Type": "application/x-www-form-urlencoded;charset=UTF-8",
    } as Record<string, string>,
    body: body.toString(),
  };
}

function buildActionURL(form: HTMLFormElement) {
  const action = String(form.getAttribute("action") || "").trim();
  return action || window.location.pathname;
}

function bindHTMXValidationHandlers() {
  if (!(doc.body instanceof HTMLElement)) {
    return;
  }
  if (doc.body.dataset.wmuValidateHTMXBound === "true") {
    return;
  }
  doc.body.dataset.wmuValidateHTMXBound = "true";

  doc.body.addEventListener("htmx:beforeRequest", (event) => {
    const form = resolveHTMXForm(event);
    if (form && isValidationAwareForm(form)) {
      clearValidationState(form);
      // ADR-0509: mark as submitting
      submittingForms.add(form);
      form.setAttribute("aria-busy", "true");
      setSubmitting(form, true);
    }
  });

  doc.body.addEventListener("htmx:afterRequest", (event) => {
    const form = resolveHTMXForm(event);
    if (form && isValidationAwareForm(form)) {
      // ADR-0509: clear submitting state after HTMX response
      clearSubmitting(form);
    }
  });

  doc.body.addEventListener("htmx:afterSwap", (event) => {
    if (event.target instanceof HTMLElement) {
      init(event.target);
    }
  });

  doc.body.addEventListener("htmx:responseError", (event) => {
    const form = resolveHTMXForm(event);
    const xhr = (event as CustomEvent<{ xhr?: XMLHttpRequest }>).detail?.xhr;
    if (!form || !xhr || !isValidationAwareForm(form)) {
      return;
    }

    // ADR-0509: clear submitting state on error responses too
    clearSubmitting(form);

    const detail = parseHXTriggerHeader(xhr.getResponseHeader("HX-Trigger"), xhr.status)
      || parseErrorDetail(parseJSONTextSafe(xhr.responseText), {
        status: xhr.status,
        statusText: xhr.statusText,
      });

    if (xhr.status === 422 || detail.fields.length > 0 && detail.descriptor?.ux_treatment === "inline_form") {
      renderValidationErrors(form, detail.fields, true);
      focusFirstInvalidField(form);
      toast(detail.message || "Please fix the highlighted fields.", toastVariantForError(detail));
      event.preventDefault();
      return;
    }

    if (shouldRedirectToLogin(detail)) {
      window.location.assign(buildLoginReturnURL());
      event.preventDefault();
      return;
    }

    renderValidationSummary(form, [{ field: "request", message: detail.message }]);
    toast(detail.message, toastVariantForError(detail));
  });
}

function resolveHTMXForm(event: Event) {
  const detail = (event as CustomEvent<{ elt?: EventTarget }>).detail;
  const source = detail?.elt;
  if (source instanceof HTMLFormElement) {
    return source;
  }
  if (source instanceof HTMLElement) {
    const parent = source.closest("form");
    if (parent instanceof HTMLFormElement) {
      return parent;
    }
  }
  const target = event.target;
  if (target instanceof HTMLFormElement) {
    return target;
  }
  return null;
}

function clearValidationState(form: HTMLFormElement) {
  form.querySelectorAll<HTMLElement>(".input-invalid, .has-error, .has-success").forEach((node) => {
    node.classList.remove("input-invalid", "has-error", "has-success");
    node.removeAttribute("aria-invalid");
    node.removeAttribute("aria-describedby");
  });

  form.querySelectorAll<HTMLElement>(".pristine-error").forEach((node) => {
    node.remove();
  });

  form.querySelectorAll<HTMLElement>("[data-field], [data-field-error-for]").forEach((node) => {
    node.textContent = "";
    node.hidden = true;
  });

  const summary = form.querySelector<HTMLElement>("[data-validation-summary]");
  if (summary) {
    summary.hidden = true;
    summary.textContent = "";
    summary.innerHTML = "";
  }
}

function clearFieldState(form: HTMLFormElement, fieldName: string) {
  const key = resolveFieldKey(fieldName);
  const input = findInputByField(form, key);
  if (input) {
    input.classList.remove("input-invalid");
    input.removeAttribute("aria-invalid");
    input.removeAttribute("aria-describedby");
    input.closest(".form-group")?.classList.remove("has-error", "has-success");
  }
  const errorNode = findErrorNode(form, key);
  if (errorNode) {
    errorNode.textContent = "";
    errorNode.hidden = true;
  }
}

function renderValidationErrors(form: HTMLFormElement, errors: ValidationError[], renderSummary = true) {
  if (!Array.isArray(errors) || errors.length === 0) {
    if (renderSummary) {
      renderValidationSummary(form, [{ field: "request", message: "Validation failed." }]);
    }
    return;
  }

  const summaryErrors: ValidationError[] = [];
  const unique = new Set<string>();

  errors.forEach((entry) => {
    const rawField = String(entry?.field || "").trim();
    const message = String(entry?.message || "Validation failed.").trim();
    const key = `${rawField}|${message}`;
    if (!message || unique.has(key)) {
      return;
    }
    unique.add(key);

    const field = resolveFieldKey(rawField);
    const input = findInputByField(form, field);
    const errorNode = findErrorNode(form, field);

    if (input) {
      input.classList.add("input-invalid");
      input.setAttribute("aria-invalid", "true");
      input.closest(".form-group")?.classList.add("has-error");
    }

    if (errorNode) {
      const errorID = ensureErrorNodeID(errorNode, field);
      errorNode.textContent = message;
      errorNode.hidden = false;
      if (input && errorID) {
        input.setAttribute("aria-describedby", errorID);
      }
    } else {
      summaryErrors.push({ field, message });
    }

    if (rawField === "" || field === "request") {
      summaryErrors.push({ field: "request", message });
    }
  });

  if (renderSummary && (summaryErrors.length > 0 || errors.length > 1)) {
    renderValidationSummary(form, summaryErrors.length > 0 ? summaryErrors : errors);
  }
}

function ensureErrorNodeID(errorNode: HTMLElement, field: string) {
  const current = String(errorNode.getAttribute("id") || "").trim();
  if (current) {
    return current;
  }
  const sanitized = String(field || "field").replace(/[^a-zA-Z0-9_-]/g, "-");
  const id = `${sanitized}-error`;
  errorNode.setAttribute("id", id);
  return id;
}

function renderValidationSummary(form: HTMLFormElement, entries: Array<ValidationError | string>) {
  const summary = form.querySelector<HTMLElement>("[data-validation-summary]");
  if (!summary) {
    return;
  }

  const normalized = (entries || [])
    .map((entry) => {
      if (typeof entry === "string") {
        return { field: "request", message: entry };
      }
      return {
        field: resolveFieldKey(entry?.field || "request"),
        message: String(entry?.message || "").trim(),
      };
    })
    .filter((entry) => entry.message);

  if (normalized.length === 0) {
    summary.hidden = true;
    summary.innerHTML = "";
    return;
  }

  summary.hidden = false;
  const list = doc.createElement("ul");
  list.className = "validation-summary-list";

  normalized.forEach((entry) => {
    const li = doc.createElement("li");
    const input = findInputByField(form, entry.field);
    if (input && input.id) {
      const link = doc.createElement("a");
      link.href = `#${input.id}`;
      link.className = "validation-summary-link";
      link.textContent = entry.message;
      link.addEventListener("click", (event) => {
        event.preventDefault();
        input.focus();
        input.scrollIntoView({ behavior: "smooth", block: "center" });
      });
      li.appendChild(link);
    } else {
      li.textContent = entry.message;
    }
    list.appendChild(li);
  });

  summary.innerHTML = "";
  summary.appendChild(list);
}

function focusFirstInvalidField(form: HTMLFormElement) {
  const first = form.querySelector<HTMLElement>(".input-invalid");
  if (first) {
    first.focus();
    return;
  }
  const fallback = form.querySelector<HTMLElement>(":invalid");
  fallback?.focus();
}

function setSubmitting(form: HTMLFormElement, submitting: boolean) {
  form.querySelectorAll('button[type="submit"], input[type="submit"]').forEach((node) => {
    if (!(node instanceof HTMLElement)) {
      return;
    }
    if (node instanceof HTMLInputElement) {
      node.disabled = submitting;
      return;
    }
    if (!(node instanceof HTMLButtonElement)) {
      return;
    }

    if (submitting) {
      node.dataset.originalText = node.textContent || "";
      node.disabled = true;
      node.classList.add("is-loading", "btn-loading");
      const loadingText = node.dataset.loadingText || node.closest("form")?.getAttribute("data-loading-text") || "";
      if (loadingText) {
        node.textContent = loadingText;
      }
      return;
    }

    node.disabled = false;
    if (node.dataset.originalText) {
      node.textContent = node.dataset.originalText;
    }
    node.classList.remove("is-loading", "btn-loading");
  });
}

function clearSubmitting(form: HTMLFormElement) {
  submittingForms.delete(form);
  form.removeAttribute("aria-busy");
  setSubmitting(form, false);
}

function parseErrorDetail(payload: Record<string, unknown>, responseLike: { status?: number; statusText?: string }) {
  return parseBrowserErrorPayload(payload, responseLike);
}

function resolveFieldKey(field?: string | null) {
  const value = String(field || "").trim();
  if (!value) return "request";
  if (value.startsWith("content.")) return "content";
  if (value.startsWith("prerequisites.")) return "prerequisites";
  if (value.startsWith("submission_schema.")) return "submission_schema";
  if (value.startsWith("branding.")) return "branding";
  const dotSplit = value.split(".")[0] || value;
  return dotSplit.split("[")[0] || dotSplit;
}

function candidateInputNames(key: string) {
  switch (key) {
    case "content":
      return ["content", "content_json"];
    case "prerequisites":
      return ["req_quest_id", "req_item_id", "req_tier_completion", "req_min_tokens", "req_min_scans"];
    case "submission_schema":
      return ["field_type"];
    case "participant_id":
      return ["participant_lookup", "participant_id"];
    default:
      return [key];
  }
}

function findInputByField(form: HTMLFormElement, key: string) {
  const contractControls = getQRFormFieldControls(form, key, activeQRFormType(form));
  if (contractControls.length > 0) {
    return contractControls[0];
  }
  const names = candidateInputNames(key);
  for (const name of names) {
    const node = form.querySelector(`[name="${cssEscape(name)}"]`);
    if (node instanceof HTMLElement) {
      return node;
    }
  }
  return null;
}

function findErrorNode(form: HTMLFormElement, key: string) {
  const selector = `[data-field="${cssEscape(key)}"], [data-field-error-for="${cssEscape(key)}"]`;
  const node = form.querySelector(selector);
  return node instanceof HTMLElement ? node : null;
}

function initQRLiveValidateButtons() {
  doc.querySelectorAll<HTMLFormElement>('form[data-qr-live-validate="true"]').forEach((form) => {
    const button = form.querySelector<HTMLButtonElement>("[data-qr-validate-button]");
    const status = form.querySelector<HTMLElement>("[data-qr-validate-status]");
    if (!button || button.dataset.wmuQrValidateBound === "true") {
      return;
    }
    button.dataset.wmuQrValidateBound = "true";

    button.addEventListener("click", async () => {
      clearValidationState(form);
      if (status) {
        status.textContent = "Validating...";
        status.style.color = "var(--text-muted)";
      }

      const params = new URLSearchParams();
      params.set("type", fieldValue(form, "type"));
      params.set("quest_id", fieldValue(form, "quest_id"));
      params.set("content", fieldValue(form, "content") || "{}");
      params.set("prerequisites", fieldValue(form, "prerequisites") || "{}");

      try {
        const response = await withNetworkProgress(fetch("/gm/qrcodes/validate", {
          method: "POST",
          credentials: "same-origin",
          headers: {
            Accept: "application/json",
            "Content-Type": "application/x-www-form-urlencoded;charset=UTF-8",
          },
          body: params.toString(),
        }));

        const payload = await parseJSONSafe(response);
        const detail = parseErrorDetail(payload, response);
        if (response.status === 422 || detail.fields.length > 0 && detail.descriptor?.ux_treatment === "inline_form") {
          renderValidationErrors(form, detail.fields, true);
          if (status) {
            status.textContent = detail.message || "Validation failed. Fix highlighted fields.";
            status.style.color = "var(--danger)";
          }
          return;
        }

        if (status) {
          status.textContent = "Validation passed.";
          status.style.color = "var(--success)";
        }
        toast("Validation passed.", "success");
      } catch {
        if (status) {
          status.textContent = "Validation unavailable right now.";
          status.style.color = "var(--warning)";
        }
        toast("Validation request failed.", "warning");
      }
    });
  });
}

function fieldValue(form: HTMLFormElement, name: string) {
  const node = form.querySelector(`[name="${cssEscape(name)}"]`);
  if (!node) return "";
  if (node instanceof HTMLInputElement && node.type === "checkbox") {
    return node.checked ? "true" : "false";
  }
  if (node instanceof HTMLSelectElement || node instanceof HTMLInputElement || node instanceof HTMLTextAreaElement) {
    return String(node.value || "").trim();
  }
  return "";
}

async function parseJSONSafe(response: Response | null) {
  if (!response) return {};
  const text = await response.text();
  return parseJSONTextSafe(text);
}

function parseJSONTextSafe(text: string) {
  const raw = String(text || "").trim();
  if (!raw) return {};
  try {
    return JSON.parse(raw) as Record<string, unknown>;
  } catch {
    return {};
  }
}

function toast(message: string, type: string) {
  window.WMUToast?.(message, type || "info");
}

function cssEscape(value: string) {
  if (window.CSS && typeof window.CSS.escape === "function") {
    return window.CSS.escape(String(value || ""));
  }
  return String(value || "").replace(/(["\\\[\]#.:])/g, "\\$1");
}

if (!window.__WMU_VALIDATE_BOOTED) {
  window.__WMU_VALIDATE_BOOTED = true;
  window.WMUValidation = {
    init,
    validateForm,
    validateField,
    clearValidationState,
    isValidationAwareForm,
  };

  if (doc.readyState === "loading") {
    doc.addEventListener("DOMContentLoaded", boot, { once: true });
  } else {
    boot();
  }
}
